import { Button } from '@/components';
import {
	EFileType,
	FILE_SIZE_10MB_BYTE,
	FILE_UPLOAD_ACCEPT,
	IMAGE_TYPE,
	PageTypeEnum,
} from '@/constant';
import { useMergingState } from '@/hooks';
import {
	CommonActions,
	DefineDocumentAreaActions,
	useAppDispatch,
} from '@/stores';
import {
	cn,
	insertObjectIf,
	isFileTypeInvalid,
	onDownloadFileFromServer,
	onDownloadFileLocal,
	showAlert,
	showToast,
} from '@/utils';
import { CloseOutlined, LoadingOutlined } from '@ant-design/icons';
import {
	ForwardedRef,
	forwardRef,
	memo,
	useCallback,
	useEffect,
	useImperativeHandle,
	useMemo,
	useRef,
} from 'react';
import { useTranslation } from 'react-i18next';

import { Spin, Upload } from 'antd';
import {
	ItemRender,
	RcFile,
	UploadChangeParam,
} from 'antd/es/upload/interface';

import './style.scss';
import type { UploadFile, UploadProps } from 'antd';
import { both, complement, filter, includes, is } from 'ramda';
import { isNonEmptyArray, isNotObject } from 'ramda-adjunct';
import { type UploadRequestOption } from 'rc-upload/lib/interface';

export type TUploadFile = UploadFile & {
	fileType?: EFileType;
	path?: string;
	id?: number;
	uid?: string;
};

interface IAttachFile
	extends Omit<UploadProps, 'defaultFileList' | 'onChange'> {
	label?: string;
	require?: boolean;
	labelClassName?: string;
	containerClassName?: string;
	horizontal?: boolean;
	limit?: number;
	maxFileSize?: number; //kb
	defaultFileList?: TUploadFile[];
	onFileChange?: (fileList: TUploadFile[]) => void;
	onFileRemove?: (id: number) => void;
	refTableName?: PageTypeEnum;
	refTableId?: string;
	hidden?: boolean;
	readOnly?: boolean;
	ignoreShowToast?: boolean;
	documentType?: string;
	callbackAllUploaded?: (responses?: any) => void;
	callbackRemoveFileSuccess?: (file?: TUploadFile) => void;
}

type TState = {
	fileList: Array<TUploadFile>;
	fileListUploaded: Array<TUploadFile>;
};

export type AttachFileRefType = {
	isUploading: boolean;
	resetFileList: () => void;
};

const formatDefaultFileList = (
	defaultFileList: IAttachFile['defaultFileList']
): IAttachFile['defaultFileList'] => {
	if (!defaultFileList) return [];
	const isObject = both(is(Object), complement(Array.isArray));
	const result = filter(isObject, defaultFileList);
	return result;
};

const AttachFile = (
	{
		label,
		require,
		labelClassName,
		containerClassName,
		horizontal = true,
		multiple = true,
		accept = FILE_UPLOAD_ACCEPT,
		limit = 10,
		maxFileSize = FILE_SIZE_10MB_BYTE,
		defaultFileList,
		onFileChange,
		onFileRemove,
		refTableName,
		refTableId,
		hidden,
		readOnly,
		ignoreShowToast = false,
		documentType,
		callbackAllUploaded,
		callbackRemoveFileSuccess,
		...props
	}: IAttachFile,
	ref: ForwardedRef<AttachFileRefType>
) => {
	const { t } = useTranslation(['common', 'validation', 'message']);

	if (hidden) {
		return null;
	}

	const dispatch = useAppDispatch();
	const [state, setState] = useMergingState<TState>({
		fileList: [] as TUploadFile[],
		fileListUploaded: [],
	});

	const hasMounted = useRef(false);
	const uploadQueue = useRef<UploadRequestOption[]>([]);
	const serverUploading = useRef(false);

	const _defaultFileList = useMemo(
		() => formatDefaultFileList(defaultFileList),
		[defaultFileList]
	);

	const isUploading = useMemo(() => {
		return state.fileList?.some((f) => f.status === 'uploading');
	}, [state.fileList]);

	const resetFileList = useCallback(() => {
		setState({ fileList: [], fileListUploaded: [] });
	}, []);

	useImperativeHandle(ref, () => {
		return {
			isUploading,
			resetFileList,
		};
	});

	useEffect(() => {
		if (isNonEmptyArray(_defaultFileList) && !hasMounted.current) {
			setState({
				fileList: _defaultFileList,
			});
			hasMounted.current = true;
		}
	}, [_defaultFileList, hasMounted.current]);

	const beforeUpload = useCallback(
		(file: RcFile, fileList: RcFile[]) => {
			const hasTooManyFiles =
				(state.fileList?.length || 0) + fileList.length > limit;

			const hasInvalidFileType = fileList.some((file) =>
				isFileTypeInvalid(accept, file.name)
			);

			const hasOversizedNonImageFiles = fileList.some(
				(file) =>
					isFileTypeInvalid(IMAGE_TYPE, file.name) && file.size > maxFileSize
			);

			if (hasTooManyFiles || hasOversizedNonImageFiles || hasInvalidFileType) {
				if (hasTooManyFiles) {
					showAlert(t('validation:ALERT_38', { maxFiles: limit }));
				}

				if (hasInvalidFileType) {
					showToast(t('message:COMMON_18'), { type: 'ERROR' });
				}

				if (hasOversizedNonImageFiles) {
					showAlert(
						t('validation:ALERT_39', { maxSize: maxFileSize / 1024 / 1024 })
					);
				}

				return Upload.LIST_IGNORE;
			}

			return true;
		},
		[t, state.fileList, limit, maxFileSize, accept]
	);

	const onChange = useCallback(
		(info: UploadChangeParam<UploadFile<TUploadFile>>) => {
			let files = [...info.fileList];

			setState((p: TState) => ({
				...p,
				fileList: files,
			}));

			const { status } = info.file;

			if (includes(status, ['done', 'error']) && files.length > 0) {
				const responses = files
					.map((f) => (f.response ?? f) as TUploadFile)
					?.filter(Boolean);

				onFileChange?.(responses);

				const allUploaded = responses.every((f) => Boolean(f.id));

				if (allUploaded) {
					if (!ignoreShowToast) {
						showToast(t('message:TOAST_01'));
					}
					callbackAllUploaded?.(responses);
				}
			}
		},
		[t, ignoreShowToast, onFileChange, callbackAllUploaded]
	);

	const processNext = useCallback(() => {
		if (serverUploading.current || uploadQueue.current.length === 0) return;

		const task = uploadQueue.current.shift();
		if (!task) return;

		serverUploading.current = true;

		const { file, onSuccess, onError } = task;

		const reqUploadDocumentList = [
			{
				mutipartFile: file as File,
				fileType: 'UPLOAD',
				...insertObjectIf(documentType, { documentType }),
			},
		];

		dispatch(
			CommonActions.uploadFileRequest({
				data: {
					reqUploadDocumentList,
					refTableName,
					refTableId,
				},
				callback: ({ data, error }: { data?: TUploadFile[]; error: any }) => {
					const uid = (file as RcFile)?.uid;
					if (error) {
						onError?.(error, {
							...(file as RcFile),
							id: uid,
						});
					} else {
						let dataUploaded = (data?.[0] || {}) as TUploadFile;
						if (dataUploaded?.id) {
							dataUploaded = {
								...dataUploaded,
								uid,
							};
						}

						onSuccess?.(dataUploaded);

						setState((prev) => ({
							...prev,
							fileListUploaded: [
								...(prev.fileListUploaded || []),
								...[dataUploaded],
							],
						}));
					}

					serverUploading.current = false;
					processNext();
				},
			})
		);
	}, [documentType, refTableName, refTableId]);

	const customRequest = (options: UploadRequestOption) => {
		const task = options;

		if (
			state.fileListUploaded?.some(
				(f) => f.uid === String((task.file as RcFile).uid)
			)
		) {
			return;
		}

		uploadQueue.current.push(task);
		processNext();
	};

	const onDownload = useCallback((file: TUploadFile) => {
		if (file?.fileType === EFileType.SERVER) {
			if (file?.path) {
				onDownloadFileFromServer(file?.path, file?.name);
			}
		} else {
			if (file?.originFileObj) {
				onDownloadFileLocal(file?.originFileObj);
			}
		}
	}, []);

	const onRemove = useCallback(
		(file: UploadFile<TUploadFile>) => {
			const response = file.response ?? (file as TUploadFile);
			const id = response?.id;

			if (!id) return;

			if (refTableId) {
				dispatch(
					DefineDocumentAreaActions.deleteDocumentsRequest({
						params: {
							refTableName,
							refTableId,
							history: refTableName === PageTypeEnum.UNIT, //avatar, companySeal has value true
							id: [Number(id)],
						},
						callback: (error) => {
							if (error) {
								return;
							}

							setState((p: TState) => ({
								...p,
								fileList: p.fileList.filter((f) => f.id !== id),
								fileListUploaded: p.fileListUploaded.filter((f) => f.id !== id),
							}));

							if (!ignoreShowToast) {
								showToast(t('message:TOAST_01'));
							}

							callbackRemoveFileSuccess?.(response);
						},
					})
				);
			} else {
				onFileRemove?.(id);
			}
		},
		[
			t,
			refTableName,
			refTableId,
			ignoreShowToast,
			onFileRemove,
			callbackRemoveFileSuccess,
		]
	);

	const itemRender: ItemRender = useCallback(
		(originNode, file, fileList, actions) => {
			if (!file || isNotObject(file)) return null;

			return (
				<div key={file.uid} className="row gap-x-2">
					<a
						className={cn(
							'text-[13px] underline',
							(file.status === 'done' ||
								//@ts-expect-error: ignore type
								file?.fileType === EFileType?.SERVER) &&
								'text-blue2',
							file.status === 'error' && 'text-red7'
						)}
						onClick={actions.download}
					>
						{file.name}
					</a>
					{file.status === 'uploading' ? (
						<Spin indicator={<LoadingOutlined spin />} />
					) : (
						<CloseOutlined
							className="text-font1 hover:text-blue2"
							onClick={actions.remove}
							hidden={readOnly || props.disabled}
						/>
					)}
				</div>
			);
		},
		[readOnly, props.disabled]
	);

	return (
		<div className={cn('row w-full', containerClassName)}>
			{label && (
				<label
					className={cn(
						'text-[13px] font-medium text-font2',
						horizontal && 'min-w-[110px]',
						labelClassName
					)}
				>
					{label} {require && <span className="text-red6">*</span>}
				</label>
			)}
			<Upload
				{...props}
				multiple={multiple}
				accept={accept}
				maxCount={limit}
				defaultFileList={_defaultFileList}
				fileList={state.fileList}
				itemRender={itemRender}
				beforeUpload={beforeUpload}
				onChange={onChange}
				onDownload={onDownload}
				customRequest={customRequest}
				onRemove={onRemove}
			>
				{!readOnly && !props.disabled && (
					<Button secondary className="min-w-min max-w-[74px]">
						{t('upload')}
					</Button>
				)}
			</Upload>
		</div>
	);
};

export default memo(forwardRef(AttachFile));
